/**
 * Utility functions to map 1SDP (Single Dynamic Package) parameters to TSX format.
 * This allows TSX to handle incoming 1SDP URLs and convert them to the correct TSX format.
 *
 * Use case: When dismissing 1SDP, TSX needs to understand and process 1SDP URLs directly.
 */

import type { ParsedUrlQuery } from 'querystring';
import {
  DETAILS_HOTEL_PREFIX,
  isDetailsHotelUrl,
  getLegacyPageName,
  LEGACY_PRODUCTS,
  stripLeadingLocale,
  isLegacyRootHotelPath,
  TSX_SUB_PAGES,
} from '@/validations/pageValidations.ts';

type RoomConfiguration = {
  adults: number;
  childrenAges: number[];
};

// The three 1SDP namespaces speak two dialects: rumbo and the whitelabels prefix every search key
// (search.departureIntervals, search.rooms[0].adults, ...) while meta traffic sends them bare. Most
// production traffic uses the prefix, and the exact lookups below only know the bare form, so those
// requests used to lose their dates and destination and fall back to the defaults. Stripping the
// prefix once, up front, is what makes the rest of this file see both dialects.
// A third dialect sends the keys all-lowercase (search.departureintervals); it is still dropped,
// because every lookup here is case-sensitive. Tracked as a follow-up, not handled by this pass.
const SEARCH_PARAM_PREFIX = 'search.';

function stripSearchPrefix(queryParams: ParsedUrlQuery): ParsedUrlQuery {
  const normalized: ParsedUrlQuery = {};

  Object.entries(queryParams).forEach(([key, value]) => {
    const hasPrefix = key.startsWith(SEARCH_PARAM_PREFIX);
    const normalizedKey = hasPrefix ? key.slice(SEARCH_PARAM_PREFIX.length) : key;

    // When a request carries both dialects for the same key, the bare one wins. A bare key that is
    // present but empty only loses to the prefixed one when it comes first in the query string; the
    // other order keeps today's behaviour, where the prefixed key was invisible anyway.
    // Object.hasOwn, not a truthiness check: an inherited `toString` is not a value we set.
    if (hasPrefix && Object.hasOwn(normalized, normalizedKey) && normalized[normalizedKey] !== '') return;

    // Deliberately NOT collapsing arrays to their last value the way hotel-detail's cleanQueryParams
    // does: parseRoomConfigurations needs the whole childrenAge array to build the room configurations.
    normalized[normalizedKey] = value;
  });

  return normalized;
}

/**
 * Parse room configurations from 1SDP query format
 * Handles rooms[0].adults=2&rooms[0].childrenAge=5&rooms[0].childrenAge=7
 */
function parseRoomConfigurations(queryParams: ParsedUrlQuery): RoomConfiguration[] {
  const rooms: RoomConfiguration[] = [];

  Object.entries(queryParams).forEach(([key, value]) => {
    const roomMatch = key.match(/rooms\[(\d+)\]\.(\w+)/);
    if (!roomMatch) return;

    const roomIndex = parseInt(roomMatch[1], 10);
    const fieldName = roomMatch[2];

    // Initialize room if it doesn't exist
    if (!rooms[roomIndex]) {
      rooms[roomIndex] = { adults: 0, childrenAges: [] };
    }

    if (fieldName === 'adults') {
      rooms[roomIndex].adults = parseInt(Array.isArray(value) ? value[0] : value || '0', 10);
    } else if (fieldName === 'childrenAge') {
      const ages = Array.isArray(value) ? value : [value];
      ages.forEach((age) => {
        const ageNum = parseInt(age || '0', 10);
        // Children over 11 are counted as adults in the system
        if (ageNum > 11) {
          rooms[roomIndex].adults += 1;
        } else {
          rooms[roomIndex].childrenAges.push(ageNum);
        }
      });
    }
  });

  // Return default if no rooms configured
  return rooms.length > 0 ? rooms : [{ adults: 2, childrenAges: [] }];
}

/**
 * Parse dates from 1SDP format
 * Supports: departureIntervals (YYYYMMDD-YYYYMMDD), leadTime + lengthOfStay, or defaults
 */
function parse1SDPDates(
  queryParams: ParsedUrlQuery,
  pageType: 'ho' | 'dp' | 'th' | 'sem' = 'ho'
): { checkin: string; checkout: string } {
  const departureIntervals = getSingleParam(queryParams, 'departureIntervals');
  const leadTime = getSingleParam(queryParams, 'leadTime');
  const lengthOfStay = getSingleParam(queryParams, 'lengthOfStay');
  const checkInOnDay = getSingleParam(queryParams, 'checkInOnDay');

  // Parse from departureIntervals
  if (departureIntervals && departureIntervals.includes('-')) {
    const [checkin, checkout] = departureIntervals.split('-');
    if (isValidDate(checkin) && isValidDate(checkout)) {
      return { checkin, checkout };
    }
  }

  // Parse from leadTime + lengthOfStay
  if (leadTime && lengthOfStay) {
    const leadTimeDays = parseInt(leadTime, 10);
    const stayDays = parseInt(lengthOfStay, 10);

    const checkinDate = new Date();
    checkinDate.setDate(checkinDate.getDate() + leadTimeDays);

    // Handle checkInOnDay if specified
    if (checkInOnDay) {
      const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const targetDay = daysOfWeek.indexOf(checkInOnDay.toLowerCase());
      if (targetDay !== -1) {
        const currentDay = checkinDate.getDay();
        const daysToAdd = (targetDay - currentDay + 7) % 7;
        if (daysToAdd > 0) {
          checkinDate.setDate(checkinDate.getDate() + daysToAdd);
        }
      }
    }

    const checkoutDate = new Date(checkinDate);
    checkoutDate.setDate(checkoutDate.getDate() + stayDays);

    return {
      checkin: formatDate(checkinDate),
      checkout: formatDate(checkoutDate),
    };
  }

  // Default dates
  const defaultLeadDays = pageType === 'sem' ? 90 : 7;
  const defaultStayDays = pageType === 'sem' ? 7 : 2;

  const checkinDate = new Date();
  checkinDate.setDate(checkinDate.getDate() + defaultLeadDays);

  const checkoutDate = new Date(checkinDate);
  checkoutDate.setDate(checkoutDate.getDate() + defaultStayDays);

  return {
    checkin: formatDate(checkinDate),
    checkout: formatDate(checkoutDate),
  };
}

/**
 * Extract hotel ID from 1SDP hotel parameter
 * Handles formats like "ILUNION-Palmanova-Mallorca-216470" or "216470".
 * Returns undefined for anything that isn't a hotel ID ("api", "_next", "hotelList", …).
 */
function extractHotelId(hotelParam?: string): string | undefined {
  if (!hotelParam) return undefined;

  // Deliberately unanchored, matching the pre-existing extraction: the last "-<digits>" run anywhere in
  // the slug is the ID, so slugs that carry a suffix after it (Hotel-216470-ES) keep resolving.
  const id = hotelParam.match(/.*-(\d+)/)?.[1] ?? hotelParam;
  return /^\d+$/.test(id) ? id : undefined;
}

// Which of the three 1SDP namespaces a path belongs to. It decides where the hotel slug sits, whether
// the URL is allowed to carry no hotel at all, and whether the static sub-pages can be preserved.
type SDPNamespace = 'hdp' | 'detailsHotel' | 'legacyRoot';

function get1SDPNamespace(pathname: string): SDPNamespace {
  if (isDetailsHotelUrl(pathname)) return 'detailsHotel';
  if (isLegacyRootHotelPath(pathname)) return 'legacyRoot';
  return 'hdp';
}

/**
 * Locate the hotel slug for the given namespace:
 * - /details/hotel/<slug>/<page>       -> path segment 3
 * - /<slug-id>/<page>                  -> path segment 1
 * - /s/hdp/hotel-details/<slug>/<page> -> the segment after hotel-details or train-hotel
 */
function findHotelSlug(pathname: string, namespace: SDPNamespace): string | undefined {
  const pathParts = pathname.split('/');
  // ['', 'details', 'hotel', '<slug>', '<page>'] - kept in step with DETAILS_HOTEL_PREFIX
  const detailsHotelSlugIndex = DETAILS_HOTEL_PREFIX.split('/').length - 1;

  if (namespace === 'detailsHotel') return pathParts[detailsHotelSlugIndex];
  if (namespace === 'legacyRoot') return pathParts[1];

  // Only hotel-details: /s/hdp is gated to /s/hdp/hotel-details/<slug>/<page>, the one shape
  // hotel-detail rewrote, so the product-first /s/hdp/train-hotel/<slug> never reaches the mapper.
  const hotelDetailsIndex = pathParts.findIndex((part) => part === 'hotel-details');
  return hotelDetailsIndex !== -1 ? pathParts[hotelDetailsIndex + 1] : undefined;
}

// The static sub-pages a legacy URL can ask for. Both are migrated to TSX and keep their own route,
// so recognising one both types the request (accommodation-only, no invented origin) and carries the
// suffix into the destination. The list lives in pageValidations next to the pattern that recognises
// the namespace, so the two cannot drift apart.
function findStaticSubPage(pageName: string): string | undefined {
  return TSX_SUB_PAGES.find((subPage) => subPage === pageName);
}

/**
 * Determine page type from 1SDP URL
 */
function determine1SDPPageType(url: string): 'ho' | 'dp' | 'th' | 'sem' {
  if (url.includes('/hotel-only') || url.includes('/sdp')) return 'ho';
  if (url.includes('/train-hotel')) return 'th';
  if (url.includes('/sem/')) return 'sem';
  if (url.includes('/hotel-details')) return 'dp';
  return 'ho'; // default
}

// The page type that sizes the default date window ('sem' gets a 90-day/7-night lookahead, everything
// else 7/2 - see parse1SDPDates). It replaces the substring-scanning determine1SDPPageType for every
// namespace, because that function has no 'sdp' case of its own - hotel-detail typed its own sdp page
// as 'sem' (mirrored in lib/loggingInfo.ts) while determine1SDPPageType lumps /sdp in with 'ho' - so
// an sdp URL used to get the 7-day window in all three namespaces. determine1SDPPageType stays as the
// fallback for shapes that name no product at all.
function determineLegacyPageType(pathname: string, legacyProduct: string): 'ho' | 'dp' | 'th' | 'sem' {
  if (legacyProduct === 'sdp') return 'sem';
  if (legacyProduct === 'hotel-only' || findStaticSubPage(legacyProduct) !== undefined) return 'ho';
  if (legacyProduct === 'train-hotel') return 'th';
  if (legacyProduct === 'flight-hotel') return 'dp';

  // Unreachable from the middleware now that every gate requires a known page name in the last
  // segment; kept as the total-function fallback, which defaults an unrecognised shape to 'dp'.
  return determine1SDPPageType(pathname);
}

/**
 * Get single parameter value from ParsedUrlQuery
 */
function getSingleParam(queryParams: ParsedUrlQuery, param: string): string | undefined {
  const value = queryParams[param];
  return Array.isArray(value) ? value[value.length - 1] : value;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

function isValidDate(dateString: string): boolean {
  if (!/^\d{8}$/.test(dateString)) return false;
  const year = parseInt(dateString.substring(0, 4), 10);
  const month = parseInt(dateString.substring(4, 6), 10);
  const day = parseInt(dateString.substring(6, 8), 10);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

/**
 * Get clickout ID from various affiliate parameters
 */
export function getClickoutId(queryParams: ParsedUrlQuery): string | undefined {
  const clickIdParams = [
    'kayakclickid',
    'clickref',
    'awc',
    'cid',
    'trv_reference',
    'bookingTrackingId',
    'skyscanner_redirectid',
    'refid',
  ];

  const foundParam = clickIdParams.find((param) => getSingleParam(queryParams, param));
  return foundParam ? getSingleParam(queryParams, foundParam) : undefined;
}

/**
 * Returns the TSX path for a mappable 1SDP page, or null when the URL names no usable hotel.
 * Only recognised shapes reach this function - the legacy app's own /api/*, /_next/* and /img/* under
 * the /s/hdp/hotel-details prefix no longer match the gate at all and 404 the way production does - so
 * null now means "the right shape carrying a slug we cannot read an id from", which the caller answers
 * with a 400 rather than a redirect into an SSR render that could only fail with a 5xx.
 */
export function map1SDPToTSXPathSync(url1SDP: string, rawQueryParams: ParsedUrlQuery): string | null {
  const queryParams = stripSearchPrefix(rawQueryParams);

  // Parse URL. The locale segment some feed hosts insert is dropped here too, so the slug and the page
  // sit where the rest of this function expects them.
  const urlObj = url1SDP.startsWith('http') ? new URL(url1SDP) : new URL(url1SDP, 'http://localhost');
  const pathname = stripLeadingLocale(urlObj.pathname);

  const namespace = get1SDPNamespace(pathname);
  const isAnchoredNamespace = namespace !== 'hdp';

  // The anchored namespaces name the product in their last segment, so read it from there rather than
  // scanning the whole path: a slug that happens to start with a product name
  // (/sdp-Hotel-Barcelona-123456/flight-hotel) would otherwise pick the wrong one. The /s/hdp namespace
  // also has shapes that put the product FIRST (/s/hdp/train-hotel/<slug>), which is why the last
  // segment alone is not enough there - it would be the slug.
  const pageName = getLegacyPageName(pathname);
  const staticSubPage = findStaticSubPage(pageName);
  const legacyProduct =
    (LEGACY_PRODUCTS as readonly string[]).includes(pageName) || staticSubPage
      ? pageName
      : (LEGACY_PRODUCTS.find((product: string) => pathname.includes(`/${product}/`)) ?? pageName);

  // Determine page type. It sizes the default date window, and hotel-detail typed its own sdp page as
  // 'sem' (a 90-day/7-night lookahead) where every other page gets 7/2 - see parse1SDPDates.
  const pageType = determineLegacyPageType(pathname, legacyProduct);
  // A static sub-page is accommodation-only by definition - hotel-detail hardcodes it that way in its
  // static-page handler. Without this it would come out as DP, and the param validation of the second
  // hop would then invent a default origin for it.
  const accommodationOnly = legacyProduct === 'hotel-only' || legacyProduct === 'sdp' || staticSubPage !== undefined;
  const isTrain = legacyProduct === 'train-hotel';

  // Extract hotel ID
  // In 1SDP URLs like /s/hdp/hotel-details/ILUNION-Palmanova-Mallorca-216470/hotel-only
  // the hotel slug is between /hotel-details/ and the final segment
  const hotelParam = getSingleParam(queryParams, 'hotel') || findHotelSlug(pathname, namespace);
  const internalHotelId = extractHotelId(hotelParam);

  // A slug we cannot read an id from means this is not a hotel page: it used to be mapped onto a
  // nonsense hotel ID (highlight_hotel_id=api). The gate now rejects the legacy app's own /api/*,
  // /_next/* and /img/* paths outright, so the only shape that still reaches here is a recognised one
  // whose slug carries no id (/details/hotel/Paris-Hotel/hotel-only). All three gates require a
  // non-empty slug, so the isAnchoredNamespace half is unreachable from the middleware and stays only
  // to keep a direct call with no slug at all from inventing a hotel.
  if ((hotelParam || isAnchoredNamespace) && !internalHotelId) return null;

  // Parse room configurations
  const roomConfigurations = parseRoomConfigurations(queryParams);

  // Parse dates
  const { checkin, checkout } = parse1SDPDates(queryParams, pageType);

  // Build TSX URLSearchParams
  const tsxParams = new URLSearchParams();

  // Core TSX parameters
  tsxParams.set('searchMode', accommodationOnly ? 'HO' : isTrain ? 'TH' : 'DP');
  // PageType is 'hotelDetail' if we have a specific hotel ID, otherwise 'search'
  // Legacy decided this by product, not by whether it had a hotel: accommodation-only landed on a
  // results list with the hotel highlighted, everything else on the hotel's own page
  // (hotel-detail's getTsxRedirectionParams: `accommodationOnly ? 'search' : 'hotelDetail'`).
  // Verified against production, which still performs this redirect itself: /hotel-only and /sdp come
  // back with pageType=search, /flight-hotel with pageType=hotelDetail.
  tsxParams.set('pageType', accommodationOnly ? 'search' : 'hotelDetail');
  tsxParams.set('source', '1sdp_direct_mapping');

  // Dates
  tsxParams.set('dateFrom', checkin.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
  tsxParams.set('dateTo', checkout.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));

  // Room configurations
  tsxParams.set('adults', roomConfigurations.map((room) => room.adults).join(';'));
  const childrenAges = roomConfigurations.map((room) => room.childrenAges.join(',')).join(';');
  if (childrenAges) {
    tsxParams.set('ages', childrenAges);
  }

  // Business profile
  const businessProfileId = getSingleParam(queryParams, 'businessProfileId');
  if (businessProfileId) {
    tsxParams.set('businessProfileId', businessProfileId);
  }

  // Branding
  const bfSource = getSingleParam(queryParams, 'bf_source') || getSingleParam(queryParams, 'bfSource');
  const bfSubsource = getSingleParam(queryParams, 'bf_subsource') || getSingleParam(queryParams, 'bfSubsource');
  if (bfSource) tsxParams.set('bf_source', bfSource);
  if (bfSubsource) tsxParams.set('bfSubsource', bfSubsource);

  // Departure airports
  const departureAirports = getSingleParam(queryParams, 'departureAirports');
  if (!accommodationOnly && departureAirports) {
    tsxParams.set('origin', departureAirports);
  }

  // Direct pass-through parameters
  const directPassParams = [
    'airline',
    'departureIntervals',
    'extReferenceType',
    'extReferenceId',
    'mealplan',
    'totalPrice',
    'currency',
    'flightClass',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'feedContext',
    'refid',
    'stops',
    'transportId',
    'int_type',
    'int_campaign',
    'int_detail',
    'skyscanner_redirectid',
    'internalIdTransport',
    'kayakclickid',
    'clickref',
    'awc',
    'cid',
    'trv_reference',
    'bookingTrackingId',
  ];

  directPassParams.forEach((paramName) => {
    const paramValue = getSingleParam(queryParams, paramName);
    if (paramValue) tsxParams.set(paramName, paramValue);
  });

  // Affiliate handling
  const affiliateRaw = queryParams.affiliate;
  let affiliateFinal: string | undefined;
  if (Array.isArray(affiliateRaw)) {
    const normalized = affiliateRaw.map((v) => String(v).toLowerCase());
    affiliateFinal = normalized.includes('yes') ? 'yes' : String(affiliateRaw[affiliateRaw.length - 1]);
  } else if (typeof affiliateRaw === 'string') {
    affiliateFinal = affiliateRaw;
  }
  if (affiliateFinal) tsxParams.set('affiliate', affiliateFinal);

  // Meta clickout ID
  const metaClickoutId = getClickoutId(queryParams);
  if (metaClickoutId) tsxParams.set('metaclickoutid', metaClickoutId);

  // Destination handling - map from 1SDP destination parameters to TSX
  const destinationCities = getSingleParam(queryParams, 'destinationCities');
  const destinationCity = getSingleParam(queryParams, 'destinationCity');
  const destinationTag = getSingleParam(queryParams, 'destinationTag');
  const destinationTags = getSingleParam(queryParams, 'destinationTags');

  const finalDestination = destinationCities || destinationCity || destinationTag || destinationTags;
  if (finalDestination) {
    tsxParams.set('destination', finalDestination);
  }

  // Hotel highlight
  if (internalHotelId) {
    tsxParams.set('highlight_hotel_id', internalHotelId);
  }

  // Build TSX path
  // Keep the sub-page the legacy URL asked for. Both hotel-static and hotel-reviews are migrated to
  // TSX and own their route, so a legacy deep link has to land on it - the same page the legacy
  // service served. MPH-959 briefly sent hotel-reviews to the plain detail page instead, because the
  // route looked blank; it was not, its content is client-fetched (hotelOpinion / guest reviews) and
  // QA has no review data for these hotels. It renders in production.
  if (internalHotelId && staticSubPage) return `/s/tsx/${internalHotelId}/${staticSubPage}?${tsxParams.toString()}`;

  // The hotel id goes in the path whenever we have one, whatever the pageType: legacy built
  // `/s/tsx/${id}?…` for every shape that resolved a hotel (getServerSidePropsForHotelPage.ts:153),
  // including the accommodation-only ones it typed as pageType=search.
  const tsxPathBase = internalHotelId ? `/s/tsx/${internalHotelId}` : '/s/tsx';
  return `${tsxPathBase}?${tsxParams.toString()}`;
}
